import { ANALYTICS_EVENTS, track as sdkTrack } from '@podo-app/analytics/react'

export { ANALYTICS_EVENTS }

// === Event Registry ===

export type PushOptInEventProperties = {
  experiment_variant: 'treatment'
  platform: 'ios' | 'android'
  permission_status: 'granted' | 'undetermined' | 'denied'
}

/**
 * JP 체험 신청 퍼널 공통 속성.
 * 화면 단계마다 싣는 속성이 달라 개별 선언 대신 공통 타입 + 인덱스 시그니처를 쓴다
 * (`page_viewed`/`button_clicked` 과 동일한 방식).
 */
export type JpTrialApplicationEventProperties = {
  lesson_language: 'ko' | 'en'
  market?: 'JP'
  [key: string]: unknown
}

export interface AnalyticsEventMap {
  jp_push_optin_action: {
    notification_id: 'N-36'
    seq: 'A1' | 'A2' | 'A3' | 'B1' | 'B2'
    action: 'opened' | 'permission_prompt' | 'settings_open' | 'granted' | 'error'
    channel: 'email'
  }

  page_viewed: {
    name: string
    location?: string
    [key: string]: unknown
  }

  popup_viewed: {
    name: string
    type?: 'bottom_sheet' | 'dialog' | 'toast' | 'full_screen'
    trigger?: string
    [key: string]: unknown
  }

  button_clicked: {
    name: string
    location?: string
    action?: string
    [key: string]: unknown
  }

  error_viewed: {
    name: string
    error_message?: string
    error_digest?: string
  }

  nps_survey_viewed: { classId: number; tutorId: number }
  nps_rating_submitted: { rating: number; classId: number; tutorId: number }
  nps_feedback_submitted: {
    rating: number
    reasons: string[]
    hasReasonText: boolean
    classId: number
    tutorId: number
  }
  nps_survey_completed: { rating: number; reasons: string[]; wantBlock: boolean; classId: number; tutorId: number }
  nps_survey_skipped: { step: string; classId: number; tutorId: number }
  tutor_blocked: { tutorId: number; classId?: number; location: string }
  tutor_block_failed: { tutorId: number; error: string; location: string }
  tutor_unblocked: { tutorId: number; location: string }
  tutor_unblock_failed: { tutorId: number; error: string; location: string }
  tutor_exclusion_action_clicked: {
    tutorId: number
    classId?: number
    location: string
    action: 'block' | 'unblock'
    source?: string
    state?: 'ready' | 'already_blocked' | 'slot_full' | 'favorited'
  }

  auto_kick_warning_shown: { classId: number; remainingSeconds: number }
  auto_kick_leave_clicked: { classId: number; remainingSeconds: number }
  auto_kick_executed: { classId: number }
  /** 서버가 내려준 수업 종료시각이 신뢰 범위를 벗어나 자동 퇴장을 끈 경우 */
  auto_kick_end_time_rejected: { classId: number; unixClassEndDatetime: number | null }

  classroom_exit_clicked: { classId: number; classType: 'PRE_STUDY' | 'CLASS' }
  classroom_exit_confirmed: {
    classId: number
    classType: 'PRE_STUDY' | 'CLASS'
    destination: 'review_complete' | 'back' | 'onetalk' | 'home'
  }
  classroom_exit_by_navigation: { classId: number; classType: 'PRE_STUDY' | 'CLASS' }

  payment_completed: {
    transaction_id: string
    amount: number
    currency: string
    item_name: string
    is_first_purchase: boolean
  }

  experiment_viewed: {
    experiment_id: string
    variation_id: number
    variation_key: string
    entity_id: string
  }

  deferred_login_store_redirect: { platform: 'ios' | 'android' }
  deferred_login_exchange_success: Record<string, never>
  /**
   * `invalid_or_expired` 는 교환 API 가 400(무효/만료/재사용 code)을 준 경우,
   * `exchange_error` 는 400 외 응답(라우팅·인프라 계통),
   * `exchange_unreachable` 은 미들웨어의 self-fetch 가 아예 실패한 경우다.
   * 셋을 한 값으로 묶으면 "성공 0 : 실패 N" 이 code 문제인지 인프라 문제인지 판별할 수 없다.
   */
  deferred_login_exchange_fail: {
    reason: 'invalid_or_expired' | 'exchange_error' | 'exchange_unreachable' | 'unknown'
  }

  card_registration_success: { location: string; paymentMethod: string }
  card_registration_failed: { location: string; paymentMethod: string; error?: string }

  profile_update_success: { location: string }
  profile_update_failed: { location: string; error?: string }

  phone_otp_request_success: { location: string }
  phone_otp_request_failed: { location: string }
  phone_otp_verify_success: { location: string }
  phone_otp_verify_failed: { location: string }

  // 계정 이메일 변경 인증. status 는 backend 결과(COOLDOWN/DUPLICATE_EMAIL 등)이며 이메일 값은 담지 않는다(PII).
  email_otp_request_success: { location: string }
  email_otp_request_failed: { location: string; status?: string }
  email_otp_verify_success: { location: string }
  email_otp_verify_failed: { location: string; status?: string }

  designated_tutor_booking_success: { tutorId: number; classId?: number; location: string }
  designated_tutor_booking_failed: { tutorId: number; location: string; error?: string }
  tutor_connect_profile_viewed: {
    tutorId: number
    classId?: number
    referrer: string
    entryPoint: 'booking_context' | 'organic'
    hasTutorConnect: boolean
    remainingCount: number
  }
  tutor_connect_profile_schedule_opened: {
    tutorId: number
    classId?: number
    referrer: string
    hasTutorConnect: boolean
    remainingCount: number
    locked: boolean
  }
  tutor_connect_schedule_date_changed: {
    surface: 'profile_schedule_sheet' | 'booking_schedule_sheet'
    tutorId?: number
    classId?: number
    selectedDate: string
    selectedDateIndex: number
    hasTutorConnect?: boolean
    locked?: boolean
  }
  tutor_connect_favorite_slot_displayed: {
    classId: number
    referrer?: string
    selectedDate: string
    favoriteSlotCount: number
    isDesignatedEdit: boolean
  }
  tutor_connect_favorite_slot_clicked: {
    classId: number
    referrer?: string
    selectedDate: string
    selectedTime: string
  }
  tutor_connect_booking_attempted: {
    tutorId: number
    classId?: number
    location: string
    source: 'profile' | 'picker' | 'booking_page' | 'direct'
    hasTutorConnect: boolean
    remainingCount: number
    selectedSlotSource?: 'recommended' | 'calendar'
  }
  tutor_connect_booking_outcome: {
    tutorId: number
    classId?: number
    location: string
    source: 'profile' | 'picker' | 'booking_page' | 'direct'
    outcome: 'success' | 'failed' | 'blocked_no_pass' | 'missing_schedule' | 'summary_loading'
    remainingCount?: number
    referrer?: string
    error?: string
    reason?: 'daily_lesson_limit'
  }
  tutor_connect_picker_card_clicked: {
    tutorId: number
    classId?: number
    locked: boolean
    hasTutorConnect: boolean
    remainingCount: number
  }
  tutor_connect_picker_favorite_clicked: {
    tutorId: number
    classId?: number
    action: 'favorite' | 'unfavorite'
    locked: boolean
  }
  tutor_connect_product_pack_selected: {
    productId: string
    ticketCount: number
    salePrice: number
    currency?: string
    isBestPack: boolean
    hasActiveSubscription: boolean
  }

  product_payment_success: { location: string; paymentMethod: string }
  product_payment_failed: { location: string; paymentMethod: string; error?: string }

  jp_payment_checkout_viewed: { sku: string; market: 'JP' }
  jp_payment_submitted: { sku: string; market: 'JP' }
  jp_payment_pg_launched: { sku: string; market: 'JP'; client_environment: 'APP' | 'WEB' }
  jp_payment_pg_launch_failed: { sku: string; market: 'JP'; errorCode: string; pgCode?: string }
  jp_payment_completed: { sku: string; market: 'JP' }
  jp_payment_failed: { sku: string; market: 'JP' }
  jp_payment_result_unknown: { sku: string | null; market: 'JP'; status: 'UNKNOWN' | 'FULFILLMENT_PENDING' }

  cta_toast_viewed: {
    trial_state: 'before_trial' | 'in_trial' | 'trial_completed' | 'paid_expired'
    label: string
    pathname: string
  }
  cta_toast_clicked: {
    trial_state: 'before_trial' | 'in_trial' | 'trial_completed' | 'paid_expired'
    label: string
    pathname: string
  }

  lesson_start_style_impression: { surface: 'standalone' | 'mic_check' }
  lesson_start_style_selected: {
    surface: 'standalone' | 'mic_check'
    selected_option: 'SKIP_INTRO' | 'WITH_INTRO'
    previous_option: 'SKIP_INTRO' | 'WITH_INTRO' | null
  }
  lesson_start_style_saved: {
    surface: 'standalone' | 'mic_check'
    saved_option: 'SKIP_INTRO' | 'WITH_INTRO'
    changed: boolean
  }

  push_primer_shown: PushOptInEventProperties
  push_primer_accepted: PushOptInEventProperties
  push_primer_declined: PushOptInEventProperties
  push_modal_shown: PushOptInEventProperties
  push_modal_clicked: PushOptInEventProperties
  push_modal_accepted: PushOptInEventProperties
  push_modal_declined: PushOptInEventProperties
  push_toast_shown: PushOptInEventProperties
  push_toast_clicked: PushOptInEventProperties
  push_toast_accepted: PushOptInEventProperties
  push_toast_declined: PushOptInEventProperties

  // === 가입/로그인 ===
  // GA4 전용이던 지점에 ClickHouse 적재를 병행한다. 이름은 GA 이벤트명과 맞춰 두 지표를 대조할 수 있게 한다.
  sign_up_completed: { image_variant: string }
  sign_in_image_viewed: { image_variant: string; country: 'KR' | 'JP' }
  sign_in_login_clicked: {
    provider: 'kakao' | 'apple' | 'line' | 'google'
    image_variant: string | null
    country: 'KR' | 'JP'
  }

  // === 구독 해지 ===
  // reason 은 사전 정의된 사유 라벨(`대분류;소분류`)만 담는다. 유저가 직접 적은 상세 사유는
  // 유저 생성 콘텐츠라 값 대신 존재 여부(has_reason_detail)만 남긴다.
  subscription_churn_completed: { subscribe_mapp_id: string; reason: string; has_reason_detail: boolean }

  // === 체험 결제 퍼널(KR) ===
  // 금액은 싣지 않는다 — subscribe_id 로 상품 카탈로그에서 집계한다.
  trial_page_viewed: {
    user_status: string
    trial_status: string
    source: string
    utm_source: string | null
    utm_medium: string | null
    utm_campaign: string | null
  }
  trial_language_selected: { language: string; subscribe_id: string }
  trial_level_selected: { level: string; language: string }
  trial_payment_method_selected: { payment_method: string; subscribe_id: string }
  trial_payment_started: {
    payment_method: string
    subscribe_id: string
    language: string
    level: string | null
    is_free: boolean
  }
  trial_payment_success: {
    payment_method: string
    subscribe_id: string
    language: string
    level: string | null
    is_free: boolean
  }
  trial_payment_error: { payment_method: string; subscribe_id: string; error: string }
  trial_free_calendar_opened: { language: string; level: string }
  trial_free_slot_clicked: {
    source: 'recommended' | 'calendar'
    language: string
    level: string
    selected: boolean
    slot_time: string | null
  }
  trial_free_booking_cta_clicked: {
    language: string | null
    level: string | null
    slot_time: string | null
    missing_item: 'language' | 'level' | 'schedule' | 'terms' | 'marketing' | null
  }
  trial_free_ticket_result: {
    result: 'success' | 'failed' | 'unknown'
    subscribe_id: string
  }
  trial_free_booking_result: {
    result: 'success' | 'slot_closed' | 'retryable' | 'failed'
    language: string
    level: string
    error_code: string | null
  }

  // === 재체험 유도 카드 ===
  re_trial_card_viewed: { round: number; is_welcome_back: boolean }
  re_trial_card_cta_clicked: { round: number; is_welcome_back: boolean }

  // === JP 체험 신청 퍼널 ===
  // GA 는 `trial_*` 로 보내지만 ClickHouse 는 KR 체험 퍼널(`trial_payment_*` 등)과 한 테이블에 쌓이므로
  // 기존 `jp_payment_*` 와 같은 방식으로 `jp_` 접두사를 붙여 시장을 구분한다.
  jp_trial_apply_view: JpTrialApplicationEventProperties
  jp_trial_language_select: JpTrialApplicationEventProperties
  jp_trial_level_select: JpTrialApplicationEventProperties
  jp_trial_slot_select: JpTrialApplicationEventProperties
  jp_trial_slot_empty_view: JpTrialApplicationEventProperties
  jp_trial_slot_refresh: JpTrialApplicationEventProperties
  jp_trial_range_expanded: JpTrialApplicationEventProperties
  jp_trial_bottomsheet_open: JpTrialApplicationEventProperties
  jp_trial_bottomsheet_confirm: JpTrialApplicationEventProperties
  jp_trial_agree_toggle: JpTrialApplicationEventProperties
  jp_trial_cta_click: JpTrialApplicationEventProperties
  jp_trial_booking_result: JpTrialApplicationEventProperties
  jp_trial_complete_view: JpTrialApplicationEventProperties
  jp_trial_post_booking_route: JpTrialApplicationEventProperties
  jp_trial_race_dialog_view: JpTrialApplicationEventProperties
  jp_trial_race_dialog_action: JpTrialApplicationEventProperties
  jp_trial_duplicate_block: JpTrialApplicationEventProperties
  jp_trial_duplicate_email_block: JpTrialApplicationEventProperties
  jp_trial_email_verification_request: JpTrialApplicationEventProperties
  jp_trial_email_verification_confirm: JpTrialApplicationEventProperties
  jp_trial_session_expired: JpTrialApplicationEventProperties
  jp_trial_cs_contact_click: JpTrialApplicationEventProperties

  // === CS/문의 ===
  // 문의 본문·제목은 유저 생성 콘텐츠라 싣지 않는다. 분류와 첨부 여부만 남긴다.
  inquiry_submitted: {
    ticket_id: string
    main_category_id: number
    sub_category_id: number
    form: 'default' | 'refund'
    attachment_count: number
    has_template_option: boolean
  }
  inquiry_submit_failed: {
    main_category_id: number
    sub_category_id: number
    form: 'default' | 'refund'
  }

  // === 화면 진입 컨텍스트 ===
  // 자동 `page_viewed`(SDK) 와 이중 계상되던 수동 호출을 옮긴 것. 경로만으로는 알 수 없는
  // 진입 컨텍스트(선택 필터·진입 경로 등)를 담는다.
  subscribes_tickets_viewed: {
    location: string
    lang_type: string
    product: string
    period: number
    is_authenticated: boolean
  }
  subscribe_payment_viewed: {
    location: string
    subscribe_id: string
    product_id: string | null
    payment_type: string
    product_name: string
  }
  onboarding_booking_viewed: { location: string; from: string | null; entry_source: string | null }
  onboarding_complete_viewed: {
    location: string
    trial_booked: boolean | null
    trial_skipped: boolean | null
    marketing_agreed: boolean | null
    from: string | null
  }
}

// === Type-safe track ===

export function track<K extends keyof AnalyticsEventMap>(eventName: K, properties: AnalyticsEventMap[K]): void {
  sdkTrack(eventName, properties as Record<string, unknown>)
}
